/**
 * SignInRequiredDialog — shown when a guest taps a feature that needs a
 * full account (join a passport, stamp a stop, redeem, contribute, etc.).
 * Styled as an obsidian editorial plate with a gold rim, matching the
 * app-wide elevation: kicker, display headline, quiet deck, clear CTAs
 * (Sign In / Create Account / Close).
 */
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { LogIn, UserPlus, Lock, X } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional feature name, e.g. "join a Community Goal". */
  feature?: string;
}

export function SignInRequiredDialog({ open, onOpenChange, feature }: Props) {
  const navigate = useNavigate();

  const goToAuth = () => {
    onOpenChange(false);
    const next = window.location.pathname + window.location.search;
    navigate(`/auth?next=${encodeURIComponent(next)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none">
        <VisuallyHidden>
          <DialogTitle>Sign in to continue</DialogTitle>
          <DialogDescription>
            {feature ? `You need an account to ${feature}.` : "You need an account to continue."}
          </DialogDescription>
        </VisuallyHidden>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.97 }}
              transition={{ type: "spring", damping: 22, stiffness: 280 }}
              className="gd-obsidian"
            >
              <button
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                className="absolute top-3 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white/60 transition hover:bg-white/[0.12] hover:text-white active:scale-95"
                style={{ touchAction: "manipulation" }}
              >
                <X className="h-4 w-4" />
              </button>

              <div className="relative p-6 sm:p-7">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 shadow-[0_10px_24px_-10px_hsl(40_90%_50%/0.7),inset_0_1px_0_hsl(45_100%_90%/0.8)]">
                    <Lock className="h-5 w-5 text-amber-950" />
                  </span>
                  <p className="gd-kicker text-amber-200/70">Members only</p>
                </div>

                <p className="gd-display pr-6 text-xl leading-snug sm:text-2xl">
                  {feature ? `Sign in to ${feature}.` : "Sign in to continue."}
                </p>
                <p className="gd-obs-muted mt-2.5 text-sm leading-relaxed">
                  Guests can browse the map, community goals and passports. Joining,
                  stamping and rewards need a free account.
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={goToAuth}
                    className="inline-flex h-11 items-center gap-1.5 rounded-full bg-gradient-to-b from-amber-300 to-amber-400 px-5 text-sm font-bold text-amber-950 shadow-[0_12px_28px_-12px_hsl(40_90%_50%/0.8),inset_0_1px_0_hsl(45_100%_92%/0.9)] transition hover:brightness-105 active:scale-[0.98]"
                    style={{ touchAction: "manipulation" }}
                  >
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </button>
                  <button
                    type="button"
                    onClick={goToAuth}
                    className="inline-flex h-11 items-center gap-1.5 rounded-full border border-white/20 bg-white/[0.06] px-5 text-sm font-semibold text-white transition hover:bg-white/[0.12] active:scale-[0.98]"
                    style={{ touchAction: "manipulation" }}
                  >
                    <UserPlus className="h-4 w-4" />
                    Create Account
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="ml-auto inline-flex h-11 items-center rounded-full px-4 text-sm font-medium text-white/55 transition hover:text-white"
                    style={{ touchAction: "manipulation" }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

export default SignInRequiredDialog;
